import { Consumer, createConsumer, Subscription } from '@rails/actioncable'
import { buildTranslate } from 'locales'
import {
  createWebSocketError,
  WebSocketErrorCodes,
  WebSocketErrorHandler,
} from 'services/websocket/errorHandling'
import type { SegmentSaveData } from 'slices/aiConversations/segmentVisualizationTypes'

const t = buildTranslate('components.ai.action_cable')

type MessagePayload = { message: string }
type SegmentPayload = { message: string; segmentData: SegmentSaveData }
type WebSocketPayload = MessagePayload | SegmentPayload

interface WebSocketReceivedData {
  type: string
  content?: string
  chat_id?: string
  [key: string]: unknown
}

interface QueuedMessage {
  type: 'message' | 'save_segment'
  payload: WebSocketPayload
  timestamp: number
}

interface RetryableMessage {
  type: 'message' | 'save_segment'
  payload: WebSocketPayload
  attempts: number
  lastAttemptAt: number
}

interface ActionCableConfig {
  token: string
  merchantId: number
  initialChatId?: string | null
}

export interface ActionCableManagerAPI {
  connect(): Promise<void>
  sendMessage(content: string): void
  saveSegment(segmentData: SegmentSaveData): void
  getChatId(): string | null
  setChatId(newChatId: string | null): void
  onMessage(callback: (data: WebSocketReceivedData) => void): void
  offMessage(callback: (data: WebSocketReceivedData) => void): void
  onMessageDropped(callback: () => void): void
  offMessageDropped(callback: () => void): void
  clearCallbacks(): void
  disconnect(): void
  connected: boolean
  startStreamingTimeout?: () => void
}

/**
 * Creates a functional ActionCable manager equivalent to the class-based ActionCableManager.
 * Maintains exact same API and behavior while using functional composition patterns.
 */
export const createActionCableManager = (
  config: ActionCableConfig
): ActionCableManagerAPI => {
  const MAX_RETRY_QUEUE_SIZE = 50
  const MAX_RETRY_AGE_MS = 5 * 60 * 1000 // 5 minutes

  let consumer: Consumer | null = null
  let subscription: Subscription | null = null
  let messageCallbacks: ((data: WebSocketReceivedData) => void)[] = []
  let messageDroppedCallbacks: (() => void)[] = []
  let isAuthenticated = false
  let chatId: string | null = config.initialChatId || null
  let messageQueue: QueuedMessage[] = []
  const maxQueueSize = 50
  const queueTimeoutMs = 30000 // 30 seconds
  let queueTimeoutId: NodeJS.Timeout | null = null
  let retryQueue: (RetryableMessage & { timestamp: number })[] = []
  const maxRetryAttempts = 3
  const baseRetryDelayMs = 1000 // 1 second base delay
  let retryTimeoutId: NodeJS.Timeout | null = null

  const clearQueueTimeout = (): void => {
    if (queueTimeoutId) {
      clearTimeout(queueTimeoutId)
      queueTimeoutId = null
    }
  }

  const clearRetryTimeout = (): void => {
    if (retryTimeoutId) {
      clearTimeout(retryTimeoutId)
      retryTimeoutId = null
    }
  }

  const processMessageQueue = (): void => {
    if (!chatId || messageQueue.length === 0) return

    // eslint-disable-next-line no-console
    console.info(
      `[ActionCableManager] Processing ${messageQueue.length} queued messages`
    )

    const queue = [...messageQueue]
    messageQueue = []

    queue.forEach(item => {
      sendImmediate(item.type, item.payload)
    })
  }

  const handleQueueTimeout = (): void => {
    if (messageQueue.length === 0) return

    // eslint-disable-next-line no-console
    console.error(
      `[ActionCableManager] Queue timeout after ${queueTimeoutMs}ms - clearing ${messageQueue.length} queued messages`
    )

    const wsError = createWebSocketError(
      'timeout',
      WebSocketErrorCodes.QUEUE_TIMEOUT,
      'Queue timeout occurred',
      { recoverable: true }
    )

    const userMessage = WebSocketErrorHandler.getMessageForUser(wsError)

    messageCallbacks.forEach(callback =>
      callback({
        type: 'error',
        content: userMessage,
      })
    )

    messageQueue = []
    queueTimeoutId = null
  }

  const startQueueTimeout = (): void => {
    clearQueueTimeout()

    queueTimeoutId = setTimeout(() => {
      handleQueueTimeout()
    }, queueTimeoutMs)
  }

  const calculateRetryDelay = (attempts: number): number => {
    // Exponential backoff: 1s, 2s, 4s, 8s, etc.
    return baseRetryDelayMs * Math.pow(2, attempts)
  }

  const cleanupRetryQueue = (): void => {
    const cutoffTime = Date.now() - MAX_RETRY_AGE_MS
    const originalLength = retryQueue.length

    retryQueue = retryQueue.filter(message => message.timestamp > cutoffTime)

    if (retryQueue.length < originalLength) {
      // eslint-disable-next-line no-console
      console.info(
        `[ActionCableManager] Cleaned up ${
          originalLength - retryQueue.length
        } old retry queue messages`
      )
    }
  }

  const scheduleRetryProcessing = (): void => {
    clearRetryTimeout()

    const nextRetry = retryQueue.reduce((earliest, message) => {
      const messageBackoff = calculateRetryDelay(message.attempts)
      const messageNextAttemptAt = message.lastAttemptAt + messageBackoff

      if (!earliest) {
        return { message, nextAttemptAt: messageNextAttemptAt }
      }

      return messageNextAttemptAt < earliest.nextAttemptAt
        ? { message, nextAttemptAt: messageNextAttemptAt }
        : earliest
    }, null as { message: RetryableMessage; nextAttemptAt: number } | null)

    if (!nextRetry) return

    const timeUntilNextAttempt = Math.max(
      0,
      nextRetry.nextAttemptAt - Date.now()
    )

    retryTimeoutId = setTimeout(() => {
      processRetryQueue()
    }, timeUntilNextAttempt)
  }

  const processRetryQueue = (): void => {
    if (retryQueue.length === 0) {
      retryTimeoutId = null
      return
    }

    const now = Date.now()
    const messagesToRetry: RetryableMessage[] = []
    const remainingMessages: RetryableMessage[] = []

    retryQueue.forEach(message => {
      const delay = calculateRetryDelay(message.attempts)
      const timeSinceLastAttempt = now - message.lastAttemptAt

      if (timeSinceLastAttempt >= delay) {
        messagesToRetry.push(message)
      } else {
        remainingMessages.push(message)
      }
    })

    messagesToRetry.forEach(message => {
      if (message.attempts >= maxRetryAttempts) {
        // Max retries reached - give up
        // eslint-disable-next-line no-console
        console.error(
          `[ActionCableManager] Max retries (${maxRetryAttempts}) reached for ${message.type}`
        )
        const wsError = createWebSocketError(
          'message',
          WebSocketErrorCodes.MAX_RETRIES_EXCEEDED,
          'Maximum retry attempts exceeded',
          { recoverable: false }
        )

        const userMessage = WebSocketErrorHandler.getMessageForUser(wsError)

        messageCallbacks.forEach(callback =>
          callback({
            type: 'error',
            content: userMessage,
          })
        )
      } else {
        // Check connectivity before attempting send
        if (!subscription || !chatId) {
          // No connectivity - update lastAttemptAt for backoff but don't increment attempts
          message.lastAttemptAt = now
          remainingMessages.push(message)
        } else {
          message.attempts++
          message.lastAttemptAt = now
          const success = sendImmediate(message.type, message.payload, true)

          if (!success) {
            remainingMessages.push(message)
          }
        }
      }
    })

    retryQueue = remainingMessages as (RetryableMessage & {
      timestamp: number
    })[]
    if (retryQueue.length > 0) {
      scheduleRetryProcessing()
    } else {
      retryTimeoutId = null
    }
  }

  const addToRetryQueue = (
    type: 'message' | 'save_segment',
    payload: WebSocketPayload
  ): void => {
    const timestampedMessage = {
      type,
      payload,
      attempts: 0,
      lastAttemptAt: Date.now(),
      timestamp: Date.now(),
    }

    cleanupRetryQueue()
    retryQueue.push(timestampedMessage)

    // Enforce size limit (remove oldest if over limit)
    if (retryQueue.length > MAX_RETRY_QUEUE_SIZE) {
      const removed = retryQueue.shift()
      // eslint-disable-next-line no-console
      console.warn(
        `[ActionCableManager] Dropped message from retry queue (queue full):`,
        removed?.payload
      )
    }

    // Start retry processing if not already running
    if (!retryTimeoutId) {
      scheduleRetryProcessing()
    }
  }

  const sendImmediate = (
    type: 'message' | 'save_segment',
    payload: WebSocketPayload,
    retrying = false
  ): boolean => {
    if (!subscription || !chatId) {
      if (!retrying) {
        // Queue for retry if not already retrying
        addToRetryQueue(type, payload)
      }
      return false
    }

    try {
      const messagePayload = {
        ...payload,
        chat_id: chatId,
      }

      subscription.send(messagePayload)

      // Start streaming timeout on successful send (initial or retried)
      const timeoutFn = api.startStreamingTimeout as (() => void) | undefined
      if (timeoutFn) {
        timeoutFn()
      }

      return true
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`[ActionCableManager] Failed to send ${type}:`, error)
      if (!retrying) {
        addToRetryQueue(type, payload)
      }
      return false
    }
  }

  const enqueueMessage = (
    type: 'message' | 'save_segment',
    payload: WebSocketPayload
  ): void => {
    if (messageQueue.length >= maxQueueSize) {
      // eslint-disable-next-line no-console
      console.error(
        `[ActionCableManager] Message queue full (${maxQueueSize}), dropping oldest message`
      )
      messageQueue.shift()
    }

    messageQueue.push({
      type,
      payload,
      timestamp: Date.now(),
    })

    // Start queue timeout if this is the first message
    if (messageQueue.length === 1 && !queueTimeoutId) {
      startQueueTimeout()
    }
  }

  const ensureReadyOrEnqueue = (
    type: 'message' | 'save_segment',
    payload: WebSocketPayload
  ): void => {
    // Check if we can send immediately
    if (subscription && isAuthenticated && chatId) {
      sendImmediate(type, payload)
      return
    }

    // Queue for later if we have subscription (even during authentication)
    if (subscription) {
      enqueueMessage(type, payload)
      return
    }

    // Cannot send - no subscription at all
    // eslint-disable-next-line no-console
    console.error(`[ActionCableManager] ${type} dropped - no subscription`, {
      hasSubscription: !!subscription,
      isAuthenticated,
      hasChatId: !!chatId,
    })

    // Trigger message dropped callbacks
    messageDroppedCallbacks.forEach(callback => callback())
  }

  // Public API implementation
  const api = {
    connect(): Promise<void> {
      return new Promise((resolve, reject) => {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000'
        const cleanApiUrl = apiUrl.replace(/\/$/, '')
        const wsUrl = cleanApiUrl
          .replace('https://', 'wss://')
          .replace('http://', 'ws://')

        consumer = createConsumer(`${wsUrl}/cable`)

        const connectTimeout = setTimeout(() => {
          api.disconnect()
          reject(new Error('Connection timeout'))
        }, 10000)

        subscription = consumer.subscriptions.create(
          {
            channel: 'SegmentChatChannel',
            token: config.token,
            merchant_id: config.merchantId,
            // Send the existing chat_id (if any) so the backend continues the
            // same conversation across reconnects instead of minting a new one
            // and orphaning the active draft. Omitted for new conversations.
            ...(chatId ? { chat_id: chatId } : {}),
          },
          {
            connected: () => {
              clearTimeout(connectTimeout)
              isAuthenticated = true
              resolve()
            },

            disconnected: () => {
              isAuthenticated = false
            },

            rejected: () => {
              clearTimeout(connectTimeout)
              reject(
                new Error(
                  'Subscription rejected - authentication may have failed'
                )
              )
            },

            received: (data: WebSocketReceivedData) => {
              if (data.type === 'connection_established') {
                isAuthenticated = true
                // Adopt the backend's chat_id only when we don't already have
                // one. On a reconnect the backend may mint a fresh chat_id;
                // overwriting our existing one would orphan the active draft
                // ("No active draft segment to submit" on save). We keep ours
                // and keep sending it, which the backend honors.
                if (data.chat_id && !chatId) {
                  chatId = data.chat_id
                }
                if (chatId) {
                  // Clear queue timeout since we have a chat_id
                  clearQueueTimeout()
                  // Process queued messages now that we have a chat_id
                  processMessageQueue()
                }
                // Forward connection_established to message callbacks
                messageCallbacks.forEach(cb => cb(data))
                return
              }

              if (data.type === 'auth_error') {
                isAuthenticated = false
                messageCallbacks.forEach(cb => cb(data))
                return
              }

              messageCallbacks.forEach(cb => cb(data))
            },
          }
        )
      })
    },

    sendMessage(content: string): void {
      ensureReadyOrEnqueue('message', { message: content })
    },

    saveSegment(segmentData: SegmentSaveData): void {
      const message = t('save_segment_message', {
        name: segmentData.name,
        data: JSON.stringify(segmentData),
      })
      ensureReadyOrEnqueue('save_segment', { message, segmentData })
    },

    getChatId(): string | null {
      return chatId
    },

    setChatId(newChatId: string | null): void {
      chatId = newChatId
    },

    onMessage(callback: (data: WebSocketReceivedData) => void): void {
      messageCallbacks.push(callback)
    },

    offMessage(callback: (data: WebSocketReceivedData) => void): void {
      const index = messageCallbacks.indexOf(callback)
      if (index > -1) {
        messageCallbacks.splice(index, 1)
      }
    },

    onMessageDropped(callback: () => void): void {
      messageDroppedCallbacks.push(callback)
    },

    offMessageDropped(callback: () => void): void {
      const index = messageDroppedCallbacks.indexOf(callback)
      if (index > -1) {
        messageDroppedCallbacks.splice(index, 1)
      }
    },

    clearCallbacks(): void {
      messageCallbacks = []
      messageDroppedCallbacks = []
    },

    disconnect(): void {
      api.clearCallbacks()
      clearQueueTimeout()
      clearRetryTimeout()

      // Clear retry queue on disconnect
      retryQueue = []

      if (subscription) {
        subscription.unsubscribe()
        subscription = null
      }

      if (consumer) {
        consumer.disconnect()
        consumer = null
      }

      isAuthenticated = false
    },

    // Optional property that can be set by useWebSocketConnection
    startStreamingTimeout: undefined,
  }

  // Add connected getter using Object.defineProperty for compatibility
  Object.defineProperty(api, 'connected', {
    get: () => isAuthenticated && consumer?.connection?.isOpen() === true,
    enumerable: true,
    configurable: false,
  })

  return api as ActionCableManagerAPI
}

// Export constructor-compatible class for backward compatibility with existing tests
export class ActionCableManager {
  private manager: ActionCableManagerAPI

  constructor(
    token: string,
    merchantId: number,
    initialChatId?: string | null
  ) {
    this.manager = createActionCableManager({
      token,
      merchantId,
      initialChatId,
    })
  }

  // Delegate all methods to the functional manager
  connect(): Promise<void> {
    return this.manager.connect()
  }

  sendMessage(content: string): void {
    this.manager.sendMessage(content)
  }

  saveSegment(segmentData: SegmentSaveData): void {
    this.manager.saveSegment(segmentData)
  }

  getChatId(): string | null {
    return this.manager.getChatId()
  }

  setChatId(newChatId: string | null): void {
    this.manager.setChatId(newChatId)
  }

  onMessage(callback: (data: WebSocketReceivedData) => void): void {
    this.manager.onMessage(callback)
  }

  offMessage(callback: (data: WebSocketReceivedData) => void): void {
    this.manager.offMessage(callback)
  }

  onMessageDropped(callback: () => void): void {
    this.manager.onMessageDropped(callback)
  }

  offMessageDropped(callback: () => void): void {
    this.manager.offMessageDropped(callback)
  }

  clearCallbacks(): void {
    this.manager.clearCallbacks()
  }

  disconnect(): void {
    this.manager.disconnect()
  }

  get connected(): boolean {
    return this.manager.connected
  }

  get startStreamingTimeout(): (() => void) | undefined {
    return this.manager.startStreamingTimeout
  }

  set startStreamingTimeout(fn: (() => void) | undefined) {
    this.manager.startStreamingTimeout = fn
  }
}
